export interface AdminAltynUserShort {
    id: number;
    name?: string;
    surname?: string;
    email?: string;
    telegram_username?: string;
    mobile?: string;
    country?: string;
}

export interface AdminAltynPaymentItem {
    id: number;
    user_id: number;
    user: AdminAltynUserShort | null;
    external_id: string;
    altyn_id: string | null;
    method: "sbp" | "bank";
    bank: string | null;
    account_number: string;
    bank_id: string | null;
    amount_rub: number;
    amount_usdt: number | null;
    rate: number | null;
    status_code: number | null;
    status_text: string | null;
    qr_url: string | null;
    deep_link: string | null;
    expires_at: string | null;
    credited_at: string | null;
    credited_amount_usdt: number | null;
    created_at: string | null;
    updated_at: string | null;
}

export interface AdminAltynPaymentDetail extends AdminAltynPaymentItem {
    processing_data: any;
    raw_webhook: any;
}

export interface AdminAltynPaymentsParams {
    per_page?: number;
    page?: number;
    sort_by?: "created_at" | "id" | "amount_rub" | "amount_usdt" | "status_code" | "user_id";
    sort_order?: "asc" | "desc";
    user_id?: number;
    phone?: string;
    account_number?: string;
    method?: "sbp" | "bank";
    bank?: string;
    external_id?: string;
    altyn_id?: string;
    status_code?: number;
    credited?: boolean;
    date_from?: string;
    date_to?: string;
}

export interface AdminAltynClientsParams {
    per_page?: number;
    page?: number;
    sort_by?: "created_at" | "id" | "user_id" | "identification_level" | "is_blocked";
    sort_order?: "asc" | "desc";
    user_id?: number;
    phone?: string;
    account_number?: string;
    is_blocked?: boolean;
    identification_level?: number;
    passport_state?: string;
    address_state?: string;
    date_from?: string;
    date_to?: string;
}

export interface AdminAltynClientItem {
    id: number;
    user_id: number;
    user: AdminAltynUserShort | null;
    phone: string;
    account_number: string | null;
    is_blocked: boolean;
    identification_level: number;
    passport_state: string | null;
    address_state: string | null;
    identification_error: string | null;
    created_at: string | null;
    updated_at: string | null;
}

export interface PaginatedMeta {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number | null;
    to: number | null;
}

export interface AdminAltynPaymentsResponse {
    success: boolean;
    data: AdminAltynPaymentItem[];
    meta: PaginatedMeta;
}

export interface AdminAltynClientsResponse {
    success: boolean;
    data: AdminAltynClientItem[];
    meta: PaginatedMeta;
}

export interface AltynMerchantBalance {
    balance: string;
    currency: string;
}

export interface AdminAltynBalanceResponse {
    success: boolean;
    data: {
        enabled: boolean;
        balance: AltynMerchantBalance | null;
    };
    message?: string;
}

export interface AdminAltynStatsResponse {
    success: boolean;
    data: {
        total_payments: string;
        total_payouts: string;
        net_balance: string;
    };
}

export interface AdminAltynPayoutItem {
    id: number;
    user_id: number;
    user: AdminAltynUserShort | null;
    external_id: string;
    altyn_id: string | null;
    account_number: string;
    amount: number;
    currency: string;
    payout_method: "crypto" | "sbp";
    wallet_address: string | null;
    sbp_bank_id: string | null;
    status_code: number | null;
    status_text: string | null;
    processing_data: any;
    created_at: string | null;
    updated_at: string | null;
    amount_rub: number | null;
    rate: number | null;
    rate_id: string | null;
    fee: number | null;
    fee_currency: string | null;
}

export interface AdminAltynPayoutDetail extends AdminAltynPayoutItem {
    raw_response: any;
}

export interface AdminAltynPayoutsParams {
    per_page?: number;
    page?: number;
    sort_by?: "created_at" | "id" | "amount" | "status_code" | "user_id";
    sort_order?: "asc" | "desc";
    user_id?: number;
    phone?: string;
    account_number?: string;
    external_id?: string;
    altyn_id?: string;
    status_code?: number;
    currency?: string;
    wallet_address?: string;
    date_from?: string;
    date_to?: string;
    fee?: number;
    fee_currency?: string;
    sbp_bank_id?: string;
    payout_method?: "crypto" | "sbp";
}

export interface AdminAltynPayoutsResponse {
    success: boolean;
    data: AdminAltynPayoutItem[];
    meta: PaginatedMeta;
}

export interface CreateAdminAltynPayoutFromWithdrawalRequest {
    request_id: number;
    rate_id?: string;
}

export const altynEndpoints = {
    getAdminAltynPayments: {
        query: (params?: AdminAltynPaymentsParams) => {
            const queryParams = new URLSearchParams();
            if (params?.per_page) queryParams.append("per_page", params.per_page.toString());
            if (params?.page) queryParams.append("page", params.page.toString());
            if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
            if (params?.sort_order) queryParams.append("sort_order", params.sort_order);
            if (params?.user_id) queryParams.append("user_id", params.user_id.toString());
            if (params?.phone) queryParams.append("phone", params.phone);
            if (params?.account_number) queryParams.append("account_number", params.account_number);
            if (params?.method) queryParams.append("method", params.method);
            if (params?.bank) queryParams.append("bank", params.bank);
            if (params?.external_id) queryParams.append("external_id", params.external_id);
            if (params?.altyn_id) queryParams.append("altyn_id", params.altyn_id);
            if (params?.status_code !== undefined) queryParams.append("status_code", params.status_code.toString());
            if (params?.credited !== undefined) queryParams.append("credited", params.credited ? "1" : "0");
            if (params?.date_from) queryParams.append("date_from", params.date_from);
            if (params?.date_to) queryParams.append("date_to", params.date_to);

            const queryString = queryParams.toString();
            return `/admin/altyn/payments${queryString ? `?${queryString}` : ""}`;
        },
        providesTags: ["AltynPayments"],
    },
    getAdminAltynClients: {
        query: (params?: AdminAltynClientsParams) => {
            const queryParams = new URLSearchParams();
            if (params?.per_page) queryParams.append("per_page", params.per_page.toString());
            if (params?.page) queryParams.append("page", params.page.toString());
            if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
            if (params?.sort_order) queryParams.append("sort_order", params.sort_order);
            if (params?.user_id) queryParams.append("user_id", params.user_id.toString());
            if (params?.phone) queryParams.append("phone", params.phone);
            if (params?.account_number) queryParams.append("account_number", params.account_number);
            if (params?.is_blocked !== undefined) queryParams.append("is_blocked", params.is_blocked ? "1" : "0");
            if (params?.identification_level !== undefined)
                queryParams.append("identification_level", params.identification_level.toString());
            if (params?.passport_state) queryParams.append("passport_state", params.passport_state);
            if (params?.address_state) queryParams.append("address_state", params.address_state);
            if (params?.date_from) queryParams.append("date_from", params.date_from);
            if (params?.date_to) queryParams.append("date_to", params.date_to);

            const queryString = queryParams.toString();
            return `/admin/altyn/clients${queryString ? `?${queryString}` : ""}`;
        },
        providesTags: ["AltynClients"],
    },
    getAdminAltynBalance: {
        query: () => "/admin/altyn/balance",
        providesTags: ["AltynBalance"],
    },
    getAdminAltynStats: {
        query: () => "/admin/altyn/stats",
        providesTags: ["AltynStats"],
    },
    getAdminAltynPayment: {
        query: (id: number) => `/admin/altyn/payments/${id}`,
        providesTags: ["AltynPayments"],
    },
    getAdminAltynPayouts: {
        query: (params?: AdminAltynPayoutsParams) => {
            const queryParams = new URLSearchParams();
            if (params?.per_page) queryParams.append("per_page", params.per_page.toString());
            if (params?.page) queryParams.append("page", params.page.toString());
            if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
            if (params?.sort_order) queryParams.append("sort_order", params.sort_order);
            if (params?.user_id) queryParams.append("user_id", params.user_id.toString());
            if (params?.phone) queryParams.append("phone", params.phone);
            if (params?.account_number) queryParams.append("account_number", params.account_number);
            if (params?.external_id) queryParams.append("external_id", params.external_id);
            if (params?.altyn_id) queryParams.append("altyn_id", params.altyn_id);
            if (params?.status_code !== undefined) queryParams.append("status_code", params.status_code.toString());
            if (params?.currency) queryParams.append("currency", params.currency);
            if (params?.wallet_address) queryParams.append("wallet_address", params.wallet_address);
            if (params?.date_from) queryParams.append("date_from", params.date_from);
            if (params?.date_to) queryParams.append("date_to", params.date_to);
            if (params?.fee) queryParams.append("fee", params.fee.toString());
            if (params?.fee_currency) queryParams.append("fee_currency", params.fee_currency);
            if (params?.sbp_bank_id) queryParams.append("sbp_bank_id", params.sbp_bank_id);
            if (params?.payout_method) queryParams.append("payout_method", params.payout_method);
            const queryString = queryParams.toString();
            return `/admin/altyn/payouts${queryString ? `?${queryString}` : ""}`;
        },
        providesTags: ["AltynPayouts"],
    },
    getAdminAltynPayout: {
        query: (id: number) => `/admin/altyn/payouts/${id}`,
        providesTags: ["AltynPayouts"],
    },
    checkAdminAltynPayout: {
        query: (id: number) => ({
            url: `/admin/altyn/payouts/${id}/check`,
            method: "POST",
        }),
        invalidatesTags: ["AltynPayouts"],
    },
    createAdminAltynCryptoPayout: {
        query: (body: CreateAdminAltynPayoutFromWithdrawalRequest) => ({
            url: "/admin/altyn/payouts/crypto",
            method: "POST",
            body,
        }),
        invalidatesTags: ["AltynPayouts", "PendingWithdrawals"],
    },
    createAdminAltynSbpPayout: {
        query: (body: CreateAdminAltynPayoutFromWithdrawalRequest) => ({
            url: "/admin/altyn/payouts/sbp",
            method: "POST",
            body,
        }),
        invalidatesTags: ["AltynPayouts", "PendingWithdrawals"],
    },
};

export const altynTags = ["AltynPayments", "AltynClients", "AltynBalance", "AltynPayouts", "AltynStats"] as const;

